import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Bell, Check, CheckCheck } from "lucide-react";

function relTime(d) {
  if (!d) return "";
  try {
    const t = new Date(d);
    const diff = (Date.now() - t.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return t.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return d;
  }
}

function dayBucket(iso) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const y = new Date(today.getTime() - 86400_000);
    const week = new Date(today.getTime() - 7 * 86400_000);
    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (sameDay(d, today)) return "Today";
    if (sameDay(d, y)) return "Yesterday";
    if (d > week) return "Earlier this week";
    return "Older";
  } catch {
    return "Older";
  }
}

const CATEGORY_DOT = {
  application_created: "bg-emerald-500",
  filing_handoff: "bg-amber-500",
  filing_approved: "bg-emerald-500",
  filing_rejected: "bg-rose-500",
  filing_withdrawn: "bg-slate-400",
  stage_change: "bg-sky-500",
  enrollment_enrolled: "bg-emerald-500",
  enrollment_withdrawn: "bg-rose-500",
  enrollment_deferred: "bg-amber-500",
  commission_commission_received: "bg-emerald-600",
  commission_invoice_sent: "bg-sky-500",
  status_change: "bg-sky-500",
  status_approved: "bg-emerald-500",
  status_refused: "bg-rose-500",
  status_withdrawn: "bg-slate-400",
  officer_assigned: "bg-orange-500",
  review_started: "bg-sky-500",
  review_changes_required: "bg-amber-500",
  review_approved: "bg-emerald-500",
  document_uploaded: "bg-slate-400",
  task_created: "bg-orange-500",
  status_expiry_7: "bg-rose-500",
  status_expiry_14: "bg-amber-500",
  docs_deadline: "bg-rose-500",
  changes_required: "bg-amber-500",
  untouched_14d: "bg-slate-400",
};

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all"); // all | unread
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/leads/my/notifications?all=true&limit=100");
      const data = r.data || {};
      // support both new {items,unread_count} and legacy array
      if (Array.isArray(data)) {
        setItems(data);
        setUnread(data.filter((n) => !n.read).length);
      } else {
        setItems(data.items || []);
        setUnread(data.unread_count || 0);
      }
    } catch {
      /* silently ignore */
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30000);
    return () => clearInterval(timerRef.current);
  }, []);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await api.post(`/leads/my/notifications/${n.id}/read`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    } catch {}
  };
  const markAll = async () => {
    try {
      await api.post(`/leads/my/notifications/read-all`);
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch {
      // fall back to per-row (older backends)
      const unreadItems = items.filter((n) => !n.read);
      for (const n of unreadItems) {
        try {
          await api.post(`/leads/my/notifications/${n.id}/read`);
        } catch {}
      }
      load();
    }
  };

  const visible = useMemo(
    () => (tab === "unread" ? items.filter((n) => !n.read) : items),
    [items, tab]
  );

  const grouped = useMemo(() => {
    const buckets = { Today: [], Yesterday: [], "Earlier this week": [], Older: [] };
    for (const n of visible) {
      const b = dayBucket(n.created_at);
      if (!buckets[b]) buckets[b] = [];
      buckets[b].push(n);
    }
    return buckets;
  }, [visible]);

  return (
    <div className="relative">
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="notif-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl border border-slate-200 shadow-lg z-40 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
              <div className="heading-font font-semibold text-slate-800 text-sm">Notifications</div>
              {unread > 0 && (
                <button
                  data-testid="notif-mark-all"
                  onClick={markAll}
                  className="text-xs text-orange-600 hover:underline flex items-center gap-1"
                >
                  <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                </button>
              )}
            </div>
            <div className="flex gap-1 px-3 pt-2 border-b border-slate-100">
              {[
                { key: "all", label: `All (${items.length})` },
                { key: "unread", label: `Unread (${unread})` },
              ].map((t) => (
                <button
                  key={t.key}
                  data-testid={`notif-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`px-3 py-1.5 text-xs rounded-t-md ${
                    tab === t.key
                      ? "bg-slate-100 text-slate-900 font-semibold"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="max-h-[28rem] overflow-y-auto">
              {visible.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-500">
                  You're all caught up. 🎉
                </div>
              )}
              {["Today", "Yesterday", "Earlier this week", "Older"].map((bucket) =>
                grouped[bucket] && grouped[bucket].length > 0 ? (
                  <div key={bucket}>
                    <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold bg-slate-50/80">
                      {bucket}
                    </div>
                    <div className="divide-y divide-slate-100">
                      {grouped[bucket].map((n) => (
                        <div
                          key={n.id}
                          data-testid={`notif-item-${n.id}`}
                          className={`px-3 py-2.5 flex items-start gap-2.5 hover:bg-slate-50 ${
                            n.read ? "opacity-60" : ""
                          }`}
                        >
                          <span
                            className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                              CATEGORY_DOT[n.category] || "bg-slate-300"
                            }`}
                          />
                          <Link
                            to={n.link || "#"}
                            onClick={() => {
                              markRead(n);
                              setOpen(false);
                            }}
                            className="flex-1 min-w-0"
                          >
                            <div className={`text-sm ${n.read ? "text-slate-600" : "text-slate-900 font-medium"}`}>
                              {n.title}
                              {n.major && (
                                <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold uppercase tracking-wider align-middle">
                                  major
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">{relTime(n.created_at)}</div>
                          </Link>
                          {!n.read && (
                            <button
                              title="Mark read"
                              onClick={() => markRead(n)}
                              className="text-slate-400 hover:text-emerald-600 shrink-0"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
